import { FormGroup, Label, Tooltip } from "@xola/ui-kit";
import { SwitchField } from "common";
import { useFormikContext } from "formik";
import { get } from "lodash";
import React, { useMemo } from "react";
import { CheckInValidationType } from "types";
import { NumberInlineValuePopover } from "../../Common/NumberInlineValuePopover";
import { PopoverListChoice } from "../Questionnaire/PopoverListChoice";
import { CheckInPreferenceFormValues } from "@/components/Product/ExperienceCheckIn/ExperienceCheckIn.types";
import {
    MultiCheckInType,
    useMultiCheckInFieldValues,
} from "@/components/Product/ExperienceCheckIn/useMultiCheckInFieldValues";
import { useVisibility } from "@/hooks/useVisibility";

interface MultipleCheckInFieldProps {
    disabled?: boolean;

    tooltip?: string;

    namePrefix?: string;

    values: CheckInPreferenceFormValues | null;
}

export const MultipleCheckInField = ({ disabled, tooltip, values, namePrefix = "" }: MultipleCheckInFieldProps) => {
    const { errors } = useFormikContext();
    const [isLimitPopoverOpen, { toggle: toggleLimitPopover }] = useVisibility(false);

    const error = useMemo(() => {
        const fieldName = `${namePrefix}validations`;
        const countValidationIndex =
            values?.validations.findIndex((validation) => validation.object === CheckInValidationType.COUNT) ?? -1;

        if (countValidationIndex === -1) return null;

        return get(errors, `${fieldName}.${countValidationIndex}.max`) ?? null;
    }, [namePrefix, errors, values?.validations]);

    const { checkInType, limit, handleMultipleCheckInEnabledToggle, handleLimitChange, handleCheckInTypeChange } =
        useMultiCheckInFieldValues(values, namePrefix);

    return (
        <FormGroup>
            <Label className={error && "!text-danger"}>Multiple Check-Ins {error}</Label>
            <Tooltip content={tooltip} disabled={!disabled}>
                <SwitchField
                    isDisabled={disabled}
                    isChecked={!!values?.multiCheckInEnabled}
                    onChange={handleMultipleCheckInEnabledToggle}
                >
                    {values?.multiCheckInEnabled ? (
                        <span
                            className="flex gap-x-1"
                            data-testid="multi-check-in-config"
                            onClick={(e) => e.stopPropagation()}
                        >
                            Allow
                            <PopoverListChoice<MultiCheckInType>
                                value={checkInType}
                                dataTestId="check-in-options"
                                disabled={disabled}
                                values={[MultiCheckInType.LIMITED, MultiCheckInType.UNLIMITED]}
                                onChange={handleCheckInTypeChange}
                            />
                            check-ins
                            {checkInType === MultiCheckInType.LIMITED && (
                                <>
                                    &nbsp;with a limit of
                                    <NumberInlineValuePopover
                                        autoSelectOnClick
                                        isOpen={isLimitPopoverOpen}
                                        disabled={disabled}
                                        value={limit}
                                        text={String(limit)}
                                        onChange={handleLimitChange}
                                        onOpenChange={toggleLimitPopover}
                                    />
                                </>
                            )}
                        </span>
                    ) : (
                        <span>Allow multiple check-ins</span>
                    )}
                </SwitchField>
            </Tooltip>
        </FormGroup>
    );
};
